import React, { useState, useRef, useEffect } from 'react';
import { Bot, User, ClipboardList, FileText, Paperclip, Search, Send } from 'lucide-react';
import {
  AIProvider,
  AIMessage,
  AIConfig,
  callAI,
  fileToBase64,
  getProviderDisplayName,
  ExtractedTimberItem,
} from '../../utils/aiService';
import { TimberItem } from '../../types/bidding';
import './BidAssistant.css';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  images?: string[];
  extractedItems?: ExtractedTimberItem[];
  isLoading?: boolean;
}

// State that can be saved and restored
export interface BidAssistantState {
  messages: ChatMessage[];
  extractedItems: ExtractedTimberItem[];
  uploadedImages: string[];
}

interface BidAssistantProps {
  onExtractedItems: (items: TimberItem[]) => void;
  onClose?: () => void;
  initialFiles?: File[];
  apiConfig?: AIConfig;
  // For preserving state between navigation
  savedState?: BidAssistantState | null;
  onSaveState?: (state: BidAssistantState) => void;
}

// Enhanced timber framing system prompt with structured output
const TIMBER_SYSTEM_PROMPT = `You are an expert timber frame estimator assistant. Your job is to analyze construction plans and extract timber requirements.

CRITICAL: When you identify timber items, you MUST output them in this EXACT format so they can be parsed:

[TIMBER_LIST_START]
QTY: 8 | SIZE: 8x8 | LENGTH: 14' | NAME: posts
QTY: 4 | SIZE: 6x10 | LENGTH: 24' | NAME: main beams
QTY: 12 | SIZE: 4x6 | LENGTH: 16' | NAME: rafters
QTY: 8 | SIZE: 6x6 | LENGTH: 8' | NAME: knee braces
[TIMBER_LIST_END]

Each line must have: QTY (quantity), SIZE (width x height in inches), LENGTH (in feet), NAME (member type).

Your expertise:
- Timber frame construction terminology
- Standard dimensions: 4x4, 4x6, 6x6, 6x8, 6x10, 8x8, 8x10, 8x12, 10x10, etc.
- Standard lengths: 8', 10', 12', 14', 16', 18', 20', 22', 24'
- Member types: posts, beams, rafters, purlins, plates, braces, knee braces, corbels, tie beams, king posts, queen posts, sills, joists, arches, headers, girts

When analyzing:
1. Identify ALL timber members in the plans
2. Extract dimensions (width × height) in inches
3. For posts: add 12-18" for embedment unless specified otherwise
4. Count carefully - watch for "TYP" (typical) which means "repeated at each location shown"
5. If unsure about anything, ASK before guessing

After outputting the timber list, explain what you found and ask any clarifying questions.`;

const BidAssistant: React.FC<BidAssistantProps> = ({
  onExtractedItems,
  onClose,
  initialFiles,
  apiConfig: externalConfig,
  savedState,
  onSaveState,
}) => {
  // Initialize from saved state if available
  const [messages, setMessages] = useState<ChatMessage[]>(savedState?.messages || []);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>(initialFiles || []);
  const [uploadedImages, setUploadedImages] = useState<string[]>(savedState?.uploadedImages || []);
  const [extractedItems, setExtractedItems] = useState<ExtractedTimberItem[]>(savedState?.extractedItems || []);
  
  // Pending upload state - files waiting to be sent with context
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [pendingImages, setPendingImages] = useState<string[]>([]);
  const [contextInput, setContextInput] = useState('');
  
  // API Configuration - Gemini uses server-side key, others need client-side
  const [provider] = useState<AIProvider>('gemini'); // Lock to Gemini (server-side)
  const [apiKey] = useState('server-side'); // Dummy value - Gemini key is stored on server
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Save state whenever it changes (for preservation between navigation)
  useEffect(() => {
    if (onSaveState && (messages.length > 0 || extractedItems.length > 0)) {
      onSaveState({
        messages,
        extractedItems,
        uploadedImages,
      });
    }
  }, [messages, extractedItems, uploadedImages, onSaveState]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Process initial files
  useEffect(() => {
    if (initialFiles && initialFiles.length > 0 && messages.length === 0) {
      handleFilesSelected(initialFiles);
    }
  }, [initialFiles]);

  const handleFilesSelected = async (files: File[]) => {
    // Convert to base64 for preview
    const base64Images: string[] = [];
    for (const file of files) {
      if (file.type.startsWith('image/') || file.type === 'application/pdf') {
        try {
          const base64 = await fileToBase64(file);
          base64Images.push(base64);
        } catch (err) {
          console.error('Error converting file:', err);
        }
      }
    }
    
    // Store as pending - don't send automatically
    setPendingFiles(prev => [...prev, ...files]);
    setPendingImages(prev => [...prev, ...base64Images]);
  };

  // Send pending files with context
  const handleSendWithContext = async () => {
    if (pendingImages.length === 0) return;
    
    // Add files to uploaded list
    setUploadedFiles(prev => [...prev, ...pendingFiles]);
    setUploadedImages(prev => [...prev, ...pendingImages]);
    
    // Create the message with user context
    const fileNames = pendingFiles.map(f => f.name).join(', ');
    const userContext = contextInput.trim();
    const messageContent = userContext 
      ? `I've uploaded: ${fileNames}\n\nContext: ${userContext}`
      : `I've uploaded: ${fileNames}`;
    
    addMessage('user', messageContent, pendingImages);
    
    // Build the analysis prompt with user context
    let analysisPrompt = 'Please analyze these documents and extract ALL timber requirements. Output them in the structured format with [TIMBER_LIST_START] and [TIMBER_LIST_END] markers.';
    if (userContext) {
      analysisPrompt = `${userContext}\n\n${analysisPrompt}`;
    }
    
    // Clear pending state
    const imagesToSend = [...pendingImages];
    setPendingFiles([]);
    setPendingImages([]);
    setContextInput('');
    
    // Send for analysis
    await analyzeDocuments(imagesToSend, analysisPrompt);
  };

  // Remove a pending file
  const handleRemovePendingFile = (index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
    setPendingImages(prev => prev.filter((_, i) => i !== index));
  };

  const addMessage = (role: 'user' | 'assistant' | 'system', content: string, images?: string[], extractedItems?: ExtractedTimberItem[]) => {
    const newMessage: ChatMessage = {
      id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      role,
      content,
      timestamp: new Date(),
      images,
      extractedItems,
    };
    setMessages(prev => [...prev, newMessage]);
    return newMessage.id;
  };

  const updateMessage = (id: string, updates: Partial<ChatMessage>) => {
    setMessages(prev => prev.map(msg => 
      msg.id === id ? { ...msg, ...updates } : msg
    ));
  };

  const analyzeDocuments = async (images: string[], userMessage: string) => {
    setIsLoading(true);
    const loadingId = addMessage('assistant', 'Analyzing your documents...');
    updateMessage(loadingId, { isLoading: true });

    try {
      const config: AIConfig = { provider, apiKey };
      
      // Build conversation history
      const aiMessages: AIMessage[] = messages
        .filter(m => m.role !== 'system' && !m.isLoading)
        .map(m => ({
          role: m.role as 'user' | 'assistant',
          content: m.content,
          images: m.images,
        }));
      
      // Add current message
      aiMessages.push({
        role: 'user',
        content: userMessage,
        images: images.length > 0 ? images : undefined,
      });

      const response = await callAI(config, aiMessages, TIMBER_SYSTEM_PROMPT);
      
      // Parse extracted items from response
      const newExtracted = parseExtractedItems(response.content);
      if (newExtracted.length > 0) {
        setExtractedItems(prev => [...prev, ...newExtracted]);
      }

      // Clean up the display content (remove the raw timber list markers for cleaner display)
      const displayContent = response.content
        .replace(/\[TIMBER_LIST_START\][\s\S]*?\[TIMBER_LIST_END\]/g, '')
        .trim();

      updateMessage(loadingId, {
        content: displayContent || response.content,
        isLoading: false,
        extractedItems: newExtracted.length > 0 ? newExtracted : undefined,
      });
    } catch (err) {
      console.error('AI error:', err);
      updateMessage(loadingId, {
        content: `Error: ${err instanceof Error ? err.message : 'Failed to analyze documents'}. Please check your API key and try again.`,
        isLoading: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    
    addMessage('user', userMessage);
    await analyzeDocuments([], userMessage);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      handleFilesSelected(files);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Enhanced parsing with multiple pattern support
  const parseExtractedItems = (content: string): ExtractedTimberItem[] => {
    const items: ExtractedTimberItem[] = [];
    const seenIds = new Set<string>();
    
    // Pattern 1: Structured format with markers
    // [TIMBER_LIST_START]
    // QTY: 8 | SIZE: 8x8 | LENGTH: 14' | NAME: posts
    // [TIMBER_LIST_END]
    const structuredMatch = content.match(/\[TIMBER_LIST_START\]([\s\S]*?)\[TIMBER_LIST_END\]/);
    if (structuredMatch) {
      const lines = structuredMatch[1].split('\n').filter(line => line.trim());
      for (const line of lines) {
        const qtyMatch = line.match(/QTY:\s*(\d+)/i);
        const sizeMatch = line.match(/SIZE:\s*(\d+)\s*x\s*(\d+)/i);
        const lengthMatch = line.match(/LENGTH:\s*(\d+['-]?\d*['"]*)/i);
        const nameMatch = line.match(/NAME:\s*([a-z\s]+)/i);
        
        if (qtyMatch && sizeMatch && lengthMatch && nameMatch) {
          const itemKey = `${qtyMatch[1]}-${sizeMatch[1]}x${sizeMatch[2]}-${lengthMatch[1]}-${nameMatch[1]}`;
          if (!seenIds.has(itemKey)) {
            seenIds.add(itemKey);
            items.push({
              id: `extracted-${Date.now()}-${items.length}`,
              quantity: parseInt(qtyMatch[1]),
              width: parseInt(sizeMatch[1]),
              height: parseInt(sizeMatch[2]),
              length: lengthMatch[1].replace(/['""]$/, "'"),
              name: nameMatch[1].trim().toLowerCase(),
              confidence: 0.95,
            });
          }
        }
      }
    }
    
    // Pattern 2: Common formats like "8x 8x8 x 14' Posts" or "8 - 8x8 x 14' posts"
    const pattern2 = /(\d+)\s*[x×\-]?\s*(\d+)\s*[x×]\s*(\d+)\s*[x×]?\s*(\d+['-]?\d*['"]*)\s+([a-z][a-z\s]*)/gi;
    let match;
    while ((match = pattern2.exec(content)) !== null) {
      const name = match[5].trim().toLowerCase();
      if (name && !name.match(/^\d/) && name.length > 1) {
        const itemKey = `${match[1]}-${match[2]}x${match[3]}-${match[4]}-${name}`;
        if (!seenIds.has(itemKey)) {
          seenIds.add(itemKey);
          items.push({
            id: `extracted-${Date.now()}-${items.length}`,
            quantity: parseInt(match[1]),
            width: parseInt(match[2]),
            height: parseInt(match[3]),
            length: match[4].replace(/['""]$/, "'"),
            name: name,
            confidence: 0.8,
          });
        }
      }
    }
    
    // Pattern 3: "Posts: 8 pieces, 8x8, 14'" or bullet points
    const pattern3 = /([a-z][a-z\s]*):\s*(\d+)\s*(?:pieces?|pcs?|ea|x)?\s*[,\-]?\s*(\d+)\s*[x×]\s*(\d+)\s*[,\-]?\s*(\d+['-]?\d*['"]*)/gi;
    while ((match = pattern3.exec(content)) !== null) {
      const name = match[1].trim().toLowerCase();
      if (name && name.length > 1) {
        const itemKey = `${match[2]}-${match[3]}x${match[4]}-${match[5]}-${name}`;
        if (!seenIds.has(itemKey)) {
          seenIds.add(itemKey);
          items.push({
            id: `extracted-${Date.now()}-${items.length}`,
            quantity: parseInt(match[2]),
            width: parseInt(match[3]),
            height: parseInt(match[4]),
            length: match[5].replace(/['""]$/, "'"),
            name: name,
            confidence: 0.75,
          });
        }
      }
    }
    
    return items;
  };

  const convertToTimberItems = (extracted: ExtractedTimberItem[]): TimberItem[] => {
    return extracted.map(item => ({
      id: item.id,
      name: item.name,
      material: item.material || '#1 GRN DF',
      quantity: item.quantity,
      width: item.width,
      height: item.height,
      realLength: item.length,
      orderLength: item.length,
      boardFeet: 0, // Will be calculated
      unitPrice: 0, // Will be fetched
      totalPrice: 0,
      confidence: item.confidence,
      manuallyEdited: false,
    }));
  };

  const handleApplyExtracted = () => {
    if (extractedItems.length === 0) return;
    const timberItems = convertToTimberItems(extractedItems);
    onExtractedItems(timberItems);
  };

  const handleRemoveExtractedItem = (id: string) => {
    setExtractedItems(prev => prev.filter(item => item.id !== id));
  };
  
  const handleEditExtractedItem = (id: string, field: keyof ExtractedTimberItem, value: string | number) => {
    setExtractedItems(prev => prev.map(item => {
      if (item.id === id) {
        return { ...item, [field]: value };
      }
      return item;
    }));
  };

  // Manual add item
  const handleAddManualItem = () => {
    const newItem: ExtractedTimberItem = {
      id: `manual-${Date.now()}`,
      quantity: 1,
      width: 8,
      height: 8,
      length: "12'",
      name: 'new item',
      confidence: 1.0,
    };
    setExtractedItems(prev => [...prev, newItem]);
  };

  const renderMessage = (message: ChatMessage) => {
    return (
      <div key={message.id} className={`chat-message ${message.role}`}>
        <div className="message-avatar">
          {message.role === 'assistant' ? <Bot size={20} /> : <User size={20} />}
        </div>
        <div className="message-content">
          {message.isLoading ? (
            <div className="loading-indicator">
              <span className="loading-dot"></span>
              <span className="loading-dot"></span>
              <span className="loading-dot"></span>
              Analyzing...
            </div>
          ) : (
            <>
              <div className="message-text">{message.content}</div>
              {message.images && message.images.length > 0 && (
                <div className="message-images">
                  {message.images.slice(0, 3).map((img, idx) => (
                    <img key={idx} src={img} alt={`Upload ${idx + 1}`} className="message-thumbnail" />
                  ))}
                  {message.images.length > 3 && (
                    <span className="more-images">+{message.images.length - 3} more</span>
                  )}
                </div>
              )}
              {message.extractedItems && message.extractedItems.length > 0 && (
                <div className="extracted-preview">
                  <strong>Found {message.extractedItems.length} timber items</strong>
                </div>
              )}
            </>
          )}
          <div className="message-time">
            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bid-assistant">
      <div className="assistant-header">
        <div className="header-title">
          <span className="assistant-icon"><Bot size={20} /></span>
          <h3>Bid Assistant</h3>
          <span className="provider-badge">AI Powered</span>
        </div>
        <div className="header-actions">
          {onClose && (
            <button className="close-btn" onClick={onClose}>×</button>
          )}
        </div>
      </div>

      <div className="chat-container" ref={chatContainerRef}>
        {messages.length === 0 && pendingFiles.length === 0 ? (
          <div className="welcome-message">
            <div className="welcome-icon"><ClipboardList size={48} /></div>
            <h4>Welcome to the Bid Assistant</h4>
            <p>
              Upload your construction plans, PDFs, or images and I'll help extract 
              the timber requirements. I can identify posts, beams, rafters, and other 
              members, then ask clarifying questions to ensure accuracy.
            </p>
            <div className="quick-start">
              <button 
                className="upload-btn-large"
                onClick={() => fileInputRef.current?.click()}
              >
                <FileText size={18} style={{ marginRight: '6px' }} />Upload Plans
              </button>
            </div>
          </div>
        ) : (
          <>
            {messages.map(renderMessage)}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Pending Files Panel - Shows when files are uploaded but not yet sent */}
      {pendingFiles.length > 0 && (
        <div className="pending-upload-panel">
          <div className="pending-header">
            <h4><Paperclip size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Ready to Analyze ({pendingFiles.length} file{pendingFiles.length > 1 ? 's' : ''})</h4>
          </div>
          
          <div className="pending-files-list">
            {pendingFiles.map((file, idx) => (
              <div key={idx} className="pending-file-item">
                {pendingImages[idx] && (
                  <img src={pendingImages[idx]} alt={file.name} className="pending-thumbnail" />
                )}
                <span className="pending-file-name">{file.name}</span>
                <span className="pending-file-size">{(file.size / 1024).toFixed(0)} KB</span>
                <button 
                  className="remove-pending-btn"
                  onClick={() => handleRemovePendingFile(idx)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          
          <div className="context-input-section">
            <label>Add context (optional):</label>
            <textarea
              value={contextInput}
              onChange={(e) => setContextInput(e.target.value)}
              placeholder="E.g., 'This is a 24x36 timber frame pavilion. Posts are 10ft with 18in embedment. Look for TYP annotations indicating repeated members...'"
              rows={3}
            />
          </div>
          
          <div className="pending-actions">
            <button 
              className="add-more-btn"
              onClick={() => fileInputRef.current?.click()}
            >
              + Add More Files
            </button>
            <button 
              className="send-for-analysis-btn"
              onClick={handleSendWithContext}
              disabled={isLoading}
            >
              {isLoading ? 'Analyzing...' : <><Search size={16} style={{ marginRight: '6px' }} />Analyze Documents</>}
            </button>
          </div>
        </div>
      )}

      {/* Extracted Items Panel - Always visible when items exist */}
      {extractedItems.length > 0 && (
        <div className="extracted-items-panel">
          <div className="panel-header">
            <h4>Extracted Items ({extractedItems.length})</h4>
            <div className="panel-actions">
              <button className="add-item-btn" onClick={handleAddManualItem} title="Add item manually">
                + Add
              </button>
              <button className="apply-btn" onClick={handleApplyExtracted}>
                Apply to Bid →
              </button>
            </div>
          </div>
          <div className="extracted-items-list">
            <table className="extracted-table">
              <thead>
                <tr>
                  <th>Qty</th>
                  <th>Size</th>
                  <th>Length</th>
                  <th>Name</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {extractedItems.map(item => (
                  <tr key={item.id}>
                    <td>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleEditExtractedItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                        min="1"
                        className="qty-input"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={`${item.width}x${item.height}`}
                        onChange={(e) => {
                          const [w, h] = e.target.value.split('x').map(v => parseInt(v) || 0);
                          if (w > 0) handleEditExtractedItem(item.id, 'width', w);
                          if (h > 0) handleEditExtractedItem(item.id, 'height', h);
                        }}
                        className="size-input"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={item.length}
                        onChange={(e) => handleEditExtractedItem(item.id, 'length', e.target.value)}
                        className="length-input"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => handleEditExtractedItem(item.id, 'name', e.target.value)}
                        className="name-input"
                      />
                    </td>
                    <td>
                      <button 
                        className="remove-item-btn"
                        onClick={() => handleRemoveExtractedItem(item.id)}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="input-area">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          accept="image/*,.pdf"
          multiple
          style={{ display: 'none' }}
        />
        <button 
          className="upload-btn"
          onClick={() => fileInputRef.current?.click()}
          title="Upload documents"
        >
          <Paperclip size={18} />
        </button>
        <textarea
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask a question or provide context (e.g., 'The posts are 12 feet tall with 18 inch embedment')..."
          rows={1}
          disabled={isLoading}
        />
        <button 
          className="send-btn"
          onClick={handleSendMessage}
          disabled={!inputValue.trim() || isLoading}
        >
          {isLoading ? '...' : <Send size={18} />}
        </button>
      </div>
    </div>
  );
};

export default BidAssistant;
